// src/pages/inventory/LowStockItems.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    TextField,
    MenuItem,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    Grid
} from '@mui/material';
import {
    Warning as WarningIcon,
    ShoppingCart as ShoppingCartIcon,
    FilterList as FilterIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';

// Componente de diálogo para registrar entrada
const RegisterEntryDialog = ({ open, onClose, item, warehouses, onSubmit, onRedirectToPurchase }) => {
    const [formData, setFormData] = useState({
        quantity: 1,
        warehouseId: '',
        notes: ''
    });
    const [error, setError] = useState('');

    const isSerialized = !!item?.equipment_type;

    useEffect(() => {
        // Resetear el formulario cuando se abre el diálogo
        if (open) {
            setFormData({
                quantity: 1,
                warehouseId: warehouses.length > 0 ? warehouses[0].id : '',
                notes: ''
            });
            setError('');
        }
    }, [open, warehouses]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = () => {
        if (!formData.warehouseId) {
            setError('Por favor seleccione un almacén');
            return;
        }

        if (formData.quantity <= 0) {
            setError('La cantidad debe ser mayor a 0');
            return;
        }

        onSubmit(item.id, formData);
    };

    if (isSerialized) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'warning.light', color: 'warning.dark', fontWeight: 'bold' }}>
                    <WarningIcon color="warning" />
                    Ingreso Controlado (Equipo Serializado)
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Producto: {item?.name} {item?.model || ''}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 2, mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
                            Este producto está catalogado como <strong>equipo serializado</strong> ({item?.equipment_type?.toUpperCase()}). Para asegurar la trazabilidad del inventario, el sistema requiere registrar de forma individual el <strong>Número de Serie (S/N)</strong> y la <strong>Dirección MAC</strong> de cada equipo.
                        </Typography>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            No se permiten entradas genéricas ni volumétricas para equipos de telecomunicaciones.
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                    <Button onClick={onClose} variant="outlined">Cancelar</Button>
                    <Button
                        onClick={() => {
                            onClose();
                            onRedirectToPurchase(item.id);
                        }}
                        variant="contained"
                        color="primary"
                        startIcon={<ShoppingCartIcon />}
                    >
                        Ir a Registrar Compra (Series)
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Registrar Entrada de Inventario</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Typography variant="subtitle1" gutterBottom>
                        <strong>Producto:</strong> {item?.name} {item?.model || ''}
                    </Typography>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: 'error.main' }}>
                        <strong>Stock Actual:</strong> {item?.quantity || 0} / <strong>Mínimo:</strong> {item?.min_stock || 0}
                    </Typography>

                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Almacén</InputLabel>
                                <Select
                                    name="warehouseId"
                                    value={formData.warehouseId}
                                    onChange={handleChange}
                                    label="Almacén"
                                >
                                    {warehouses.map(warehouse => (
                                        <MenuItem key={warehouse.id} value={warehouse.id}>
                                            {warehouse.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="Cantidad"
                                name="quantity"
                                type="number"
                                value={formData.quantity}
                                onChange={handleChange}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Notas"
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                placeholder="Detalles adicionales sobre esta entrada"
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                >
                    Registrar Entrada
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const LowStockItems = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [filterCategory, setFilterCategory] = useState('');
    const [filterWarehouse, setFilterWarehouse] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const handleRedirectToPurchase = (itemId) => {
        navigate('/inventory/purchases/new', { state: { prefilledItemId: itemId } });
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');

            // Fetch low stock items
            const itemsResponse = await fetch('/api/inventory/items/low-stock', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!itemsResponse.ok) {
                throw new Error('Error al obtener productos con bajo stock');
            }

            const itemsData = await itemsResponse.json();
            setItems(itemsData);

            // Fetch categories
            const categoriesResponse = await fetch('/api/inventory/categories', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!categoriesResponse.ok) {
                throw new Error('Error al obtener categorías');
            }

            const categoriesData = await categoriesResponse.json();
            setCategories(categoriesData);

            // Fetch warehouses
            const warehousesResponse = await fetch('/api/inventory/warehouses', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!warehousesResponse.ok) {
                throw new Error('Error al obtener almacenes');
            }

            const warehousesData = await warehousesResponse.json();
            setWarehouses(warehousesData);

        } catch (error) {
            console.error('Error fetching data:', error);
            setError(error.message || 'Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleRegisterEntry = async (itemId, formData) => {
        try {
            const token = localStorage.getItem('token');

            // Verificar si hay un token
            if (!token) {
                alert('No se encontró el token de autenticación. Por favor inicie sesión nuevamente.');
                return;
            }

            const response = await fetch('/api/inventory/transactions', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    item_id: itemId,
                    warehouse_id: formData.warehouseId,
                    quantity: formData.quantity,
                    transaction_type: 'entrada',
                    reference_type: 'stock_adjustment',
                    notes: formData.notes || 'Entrada registrada desde Stock Bajo'
                })
            });

            if (!response.ok) {
                // Si es un error de autenticación (401), manejar específicamente
                if (response.status === 401) {
                    alert('Su sesión ha expirado. Por favor inicie sesión nuevamente.');
                    // Redireccionar al login
                    window.location.href = '/login';
                    return;
                }

                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al registrar entrada');
            }

            // Si todo va bien, cerrar diálogo, mostrar mensaje y actualizar lista
            setDialogOpen(false);
            setSelectedItem(null);
            alert('Entrada registrada correctamente');
            fetchData(); // Actualizar datos
        } catch (error) {
            console.error('Error registering entry:', error);
            alert(error.message);
        }
    };

    // Filtrar items según los filtros seleccionados
    const filteredItems = items.filter(item => {
        let matches = true;

        if (filterCategory && item.category_id !== filterCategory) {
            matches = false;
        }

        if (filterWarehouse && item.warehouse_id !== filterWarehouse) {
            matches = false;
        }

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const nameMatch = item.name.toLowerCase().includes(searchLower);
            const modelMatch = item.model && item.model.toLowerCase().includes(searchLower);
            const brandMatch = item.brand && item.brand.toLowerCase().includes(searchLower);
            const skuMatch = item.sku && item.sku.toLowerCase().includes(searchLower);

            if (!(nameMatch || modelMatch || brandMatch || skuMatch)) {
                matches = false;
            }
        }

        return matches;
    });

    // Calcular stock status level para indicadores visuales
    const getStockStatusLevel = (item) => {
        const ratio = item.quantity / item.min_stock;

        if (ratio === 0) return 'out';
        if (ratio <= 0.5) return 'critical';
        if (ratio < 1) return 'low';
        return 'normal';
    };

    const getStockChip = (item) => {
        const level = getStockStatusLevel(item);

        switch (level) {
            case 'out':
                return <Chip
                    label="Sin Stock"
                    color="error"
                    icon={<WarningIcon />}
                    variant="outlined"
                />;
            case 'critical':
                return <Chip
                    label="Crítico"
                    color="error"
                    icon={<WarningIcon />}
                />;
            case 'low':
                return <Chip
                    label="Bajo"
                    color="warning"
                    icon={<WarningIcon />}
                />;
            default:
                return <Chip
                    label="Normal"
                    color="success"
                />;
        }
    };

    if (loading && items.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                <WarningIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
                Productos con Stock Bajo
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 2, flexGrow: 1 }}>
                    <TextField
                        placeholder="Buscar productos..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                        sx={{ width: '250px' }}
                    />

                    <TextField
                        select
                        label="Categoría"
                        size="small"
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        sx={{ width: '200px' }}
                        InputProps={{
                            startAdornment: <FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                    >
                        <MenuItem value="">Todas</MenuItem>
                        {categories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                                {category.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        label="Almacén"
                        size="small"
                        value={filterWarehouse}
                        onChange={(e) => setFilterWarehouse(e.target.value)}
                        sx={{ width: '200px' }}
                        InputProps={{
                            startAdornment: <FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {warehouses.map((warehouse) => (
                            <MenuItem key={warehouse.id} value={warehouse.id}>
                                {warehouse.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <IconButton
                        color="primary"
                        onClick={fetchData}
                        title="Actualizar"
                    >
                        <RefreshIcon />
                    </IconButton>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    component={Link}
                    to="/inventory/purchases/new"
                    startIcon={<ShoppingCartIcon />}
                >
                    Crear Orden de Compra
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Producto</TableCell>
                            <TableCell>Categoría</TableCell>
                            <TableCell>Almacén</TableCell>
                            <TableCell align="center">Stock Actual</TableCell>
                            <TableCell align="center">Stock Mínimo</TableCell>
                            <TableCell align="center">Estado</TableCell>
                            <TableCell align="right">Precio Compra</TableCell>
                            <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredItems.length > 0 ? (
                            filteredItems.map((item) => (
                                <TableRow key={`${item.id}-${item.warehouse_id}`}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {item.name}
                                        </Typography>
                                        {item.model && (
                                            <Typography variant="caption" component="div" color="text.secondary">
                                                {item.brand} {item.model}
                                            </Typography>
                                        )}
                                        {item.sku && (
                                            <Typography variant="caption" component="div" color="text.secondary">
                                                SKU: {item.sku}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{item.category_name}</TableCell>
                                    <TableCell>{item.warehouse_name}</TableCell>
                                    <TableCell align="center">
                                        <Typography
                                            sx={{
                                                fontWeight: 'bold',
                                                color: item.quantity === 0 ? 'error.main' :
                                                    item.quantity < item.min_stock ? 'warning.main' : 'inherit'
                                            }}
                                        >
                                            {item.quantity}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">{item.min_stock}</TableCell>
                                    <TableCell align="center">
                                        {getStockChip(item)}
                                    </TableCell>
                                    <TableCell align="right">
                                        ${parseFloat(item.purchase_price).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            onClick={() => {
                                                setSelectedItem(item);
                                                setDialogOpen(true);
                                            }}
                                            startIcon={<AddIcon />}
                                        >
                                            Registrar Entrada
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    {loading ? (
                                        <CircularProgress size={24} sx={{ my: 1 }} />
                                    ) : (
                                        'No hay productos con stock bajo'
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Dialog for adding stock */}
            {selectedItem && (
                <RegisterEntryDialog
                    open={dialogOpen}
                    onClose={() => {
                        setDialogOpen(false);
                        setSelectedItem(null);
                    }}
                    item={selectedItem}
                    warehouses={warehouses}
                    onSubmit={handleRegisterEntry}
                    onRedirectToPurchase={handleRedirectToPurchase}
                />
            )}
        </Box>
    );
};

export default LowStockItems;